import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Search,
  Bell,
  AlertCircle,
  CalendarDays,
  CheckCircle2,
  CircleDollarSign,
  BriefcaseBusiness,
  Gauge
} from 'lucide-react'
import { supabase } from '../supabase'
import { useUser } from '../UserContext'
import {
  daysUntil,
  getDeliveryDueDate,
  isCompleteStatus
} from '../lib/healthStatus'
import { buildAlerts } from '../lib/alerts'

function currencySymbol(c) {
  return c === 'GBP' ? '£' : c === 'EUR' ? '€' : '$'
}

function formatMoney(value, currency = 'GBP') {
  const amount = Number(value || 0)

  if (amount >= 1000000) {
    return `${currencySymbol(currency)}${(amount / 1000000).toFixed(2)}M`
  }

  if (amount >= 1000) {
    return `${currencySymbol(currency)}${Math.round(amount / 1000)}k`
  }

  return `${currencySymbol(currency)}${amount.toLocaleString()}`
}
// eslint-disable-next-line no-unused-vars
function formatDate(d) {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

export default function Dashboard() {
  const navigate = useNavigate()
  const { orgId, can } = useUser()
  const canViewFinancials = can('financials.view')

  const [loading, setLoading] = useState(true)
  const [clients, setClients] = useState([])
  const [portfolios, setPortfolios] = useState([])
  const [projects, setProjects] = useState([])
  const [deals, setDeals] = useState([])
  const [deliveries, setDeliveries] = useState([])
  const [allocations, setAllocations] = useState([])
  const [dashboardView, setDashboardView] = useState('overall')
  const [selectedPortfolioId, setSelectedPortfolioId] = useState('')
  const [selectedCurrency, setSelectedCurrency] = useState('GBP')
  const [globalSearch, setGlobalSearch] = useState('')
  const [searchFocused, setSearchFocused] = useState(false)
  const [alertsOpen, setAlertsOpen] = useState(false)

  useEffect(() => {
    fetchDashboard()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId])

  async function fetchDashboard() {
    if (!orgId) return

    setLoading(true)

    const [
      { data: clientData },
      { data: portfolioData },
      { data: projectData },
      { data: dealData },
      { data: deliveryData },
      { data: allocationData }
    ] = await Promise.all([
      supabase
        .from('clients')
        .select('id, name, created_at, org_id')
        .eq('org_id', orgId),

      supabase
        .from('portfolios')
        .select('id, name, description, status, created_at, org_id')
        .eq('org_id', orgId)
        .order('name'),

      supabase
        .from('secure_projects')
        .select('id, name, portfolio_id, start_date, end_date, created_at, org_id')
        .eq('org_id', orgId),

      supabase
        .from('secure_deals')
        .select('id, client_id, name, value, currency, status, end_date, created_at, org_id')
        .eq('org_id', orgId),

      supabase
        .from('secure_deliveries')
        .select('id, name, status, delivery_date, custom_fields, created_at, org_id, rights(name, project_id, deal_id, deals(id, name, clients(name)))')
        .eq('org_id', orgId),

      supabase
        .from('secure_deal_project_allocations')
        .select('id, amount, currency, project_id, deal_id, org_id')
        .eq('org_id', orgId)
    ])

    setClients(clientData || [])
    setPortfolios(portfolioData || [])
    setProjects(projectData || [])
    setDeals(dealData || [])
    setDeliveries(deliveryData || [])
    setAllocations(allocationData || [])
    setLoading(false)
  }

  const activePortfolios = portfolios.filter(portfolio => portfolio.status !== 'archived')
  const selectedPortfolio = portfolios.find(portfolio => portfolio.id === selectedPortfolioId) || activePortfolios[0] || portfolios[0] || null
  const portfolioMode = dashboardView === 'portfolio' && !!selectedPortfolio

  useEffect(() => {
    if (dashboardView !== 'portfolio') return
    if (selectedPortfolioId) return
    const defaultPortfolio = activePortfolios[0] || portfolios[0]
    if (defaultPortfolio) setSelectedPortfolioId(defaultPortfolio.id)
  }, [dashboardView, selectedPortfolioId, activePortfolios, portfolios])

  const dashboardProjects = portfolioMode
    ? projects.filter(project => project.portfolio_id === selectedPortfolio.id)
    : projects

  const dashboardProjectIds = new Set(dashboardProjects.map(project => project.id))

  const dashboardAllocations = portfolioMode
    ? allocations.filter(allocation => dashboardProjectIds.has(allocation.project_id))
    : allocations

  const dashboardDealIds = new Set(dashboardAllocations.map(allocation => allocation.deal_id).filter(Boolean))

  const dashboardDeals = portfolioMode
    ? deals.filter(deal => dashboardDealIds.has(deal.id))
    : deals

  const dashboardDeliveries = portfolioMode
    ? deliveries.filter(delivery => dashboardProjectIds.has(delivery.rights?.project_id))
    : deliveries

  const dashboardCurrencies = useMemo(() => {
    const currencies = new Set()

    dashboardDeals.forEach(deal => currencies.add(deal.currency || 'GBP'))
    dashboardAllocations.forEach(allocation => currencies.add(allocation.currency || 'GBP'))

    return [...currencies].sort()
  }, [dashboardDeals, dashboardAllocations])

  useEffect(() => {
    if (dashboardCurrencies.length === 0) return
    if (dashboardCurrencies.includes(selectedCurrency)) return
    setSelectedCurrency(dashboardCurrencies[0])
  }, [dashboardCurrencies, selectedCurrency])

  const currencyDeals = dashboardDeals.filter(deal => (deal.currency || 'GBP') === selectedCurrency)
  const currencyAllocations = dashboardAllocations.filter(allocation => (allocation.currency || 'GBP') === selectedCurrency)

  const dashboardTitle = portfolioMode ? selectedPortfolio.name : 'Dashboard'
  const dashboardSubtitle = portfolioMode
    ? 'Portfolio view across projects, deals, rights and delivery performance'
    : 'Overall view across every project, deal and delivery'

  const activeDeals = dashboardDeals.filter(d => d.status === 'active')
  const currencyActiveDeals = currencyDeals.filter(d => d.status === 'active')
  const activeProjects = dashboardProjects.length

  const totalRevenue = portfolioMode
    ? currencyAllocations.reduce((sum, allocation) => sum + (Number(allocation.amount) || 0), 0)
    : currencyDeals.reduce((sum, deal) => {
    return sum + (Number(deal.value) || 0)
  }, 0)

  const activeProjectRevenue = currencyAllocations.reduce((sum, allocation) => {
    return sum + (Number(allocation.amount) || 0)
  }, 0)

  const completedDeliveries = dashboardDeliveries.filter(d => isCompleteStatus(d.status)).length

  const completionRate = dashboardDeliveries.length > 0
    ? Math.round((completedDeliveries / dashboardDeliveries.length) * 100)
    : 0

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const overdueDeliveries = dashboardDeliveries.filter(d => {
    const dueDate = getDeliveryDueDate(d)

    return (
      dueDate &&
      dueDate < today &&
      !isCompleteStatus(d.status)
    )
  })

  const dueSoonDeliveries = dashboardDeliveries.filter(d => {
    const dueDate = getDeliveryDueDate(d)
    const days = daysUntil(dueDate)

    return (
      dueDate &&
      days !== null &&
      days >= 0 &&
      days <= 30 &&
      !isCompleteStatus(d.status)
    )
  })

  const projectRevenue = useMemo(() => {
    const map = {}

    currencyAllocations.forEach(allocation => {
      const name = allocation.projects?.name || 'Other Projects'
      map[name] = (map[name] || 0) + (Number(allocation.amount) || 0)
    })

    return Object.entries(map)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 4)
  }, [currencyAllocations])

  const maxProjectRevenue = Math.max(...projectRevenue.map(p => p.value), 1)

  const deliveryHealth = {
    delivered: dashboardDeliveries.filter(d => isCompleteStatus(d.status)).length,
    pending: dashboardDeliveries.filter(d => !isCompleteStatus(d.status)).length,
    overdue: overdueDeliveries.length
  }

  const clientNames = new Map(clients.map(client => [client.id, client.name]))
  const alertDeals = activeDeals.map(deal => ({ ...deal, client_name: clientNames.get(deal.client_id) }))
  const alerts = buildAlerts({ deliveries: dashboardDeliveries, deals: alertDeals })
  const alertPreview = alerts.slice(0, 5)

  const upcomingDeadlines = [
    ...dashboardDeliveries
      .map(d => ({
        ...d,
        dueDate: getDeliveryDueDate(d)
      }))
      .filter(d => d.dueDate && d.dueDate >= today && d.status !== 'delivered' && d.status !== 'approved')
      .map(d => ({
        date: d.dueDate,
        title: d.name || d.rights?.name || 'Delivery',
        subtitle: d.rights?.deals?.name || 'Deal',
        type: 'delivery'
      })),
    ...activeDeals
      .filter(d => d.end_date && d.end_date >= today)
      .map(d => ({
        date: d.end_date,
        title: d.name,
        subtitle: d.clients?.name || 'Client',
        type: 'deal'
      }))
  ]
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .slice(0, 4)

  const recentActivity = [
    ...dashboardDeals.slice(0, 3).map(d => ({
      title: d.name,
      subtitle: `${d.clients?.name || 'Client'} · deal created`,
      time: 'Recently'
    })),
    ...dashboardDeliveries.slice(0, 3).map(d => ({
      title: d.rights?.name || 'Delivery',
      subtitle: `${d.status || 'pending'} delivery`,
      time: 'Recently'
    }))
  ].slice(0, 5)

  const globalResults = globalSearch.trim()
  ? [
      ...deals
        .filter(d =>
          (d.name || '').toLowerCase().includes(globalSearch.toLowerCase()) ||
          (d.clients?.name || '').toLowerCase().includes(globalSearch.toLowerCase())
        )
        .slice(0, 5)
        .map(d => ({
          id: d.id,
          type: 'Deal',
          title: d.name,
          subtitle: d.clients?.name || 'No client',
          path: `/deals/${d.id}`
        })),

      ...projects
        .filter(p =>
          (p.name || '').toLowerCase().includes(globalSearch.toLowerCase())
        )
        .slice(0, 5)
        .map(p => ({
          id: p.id,
          type: 'Project',
          title: p.name,
          subtitle: 'Project workspace',
          path: `/projects/${p.id}`
        })),

      ...clients
        .filter(c =>
          (c.name || '').toLowerCase().includes(globalSearch.toLowerCase())
        )
        .slice(0, 5)
        .map(c => ({
          id: c.id,
          type: 'Client',
          title: c.name,
          subtitle: 'Client directory',
          path: `/clients/${c.id}`
        }))
    ].slice(0, 8)
  : []

function SkeletonBlock({ height = 120, radius = 14 }) {
  return (
    <div
      style={{
        height,
        borderRadius: radius,
        background: 'linear-gradient(90deg, #F1F5F9 25%, #E2E8F0 50%, #F1F5F9 75%)',
        backgroundSize: '200% 100%',
        animation: 'dash-shimmer 1.4s infinite',
        border: '1px solid var(--hv-border)'
      }}
    />
  )
}

if (loading) {
  return (
    <div className="hv-page">
      <style>{`
        @keyframes dash-shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }

        .dash-skeleton-grid-4 {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
          margin-bottom: 16px;
        }

        .dash-skeleton-grid-main {
          display: grid;
          grid-template-columns: 1.4fr 1fr 0.9fr;
          gap: 16px;
          margin-bottom: 16px;
        }

        .dash-skeleton-grid-bottom {
          display: grid;
          grid-template-columns: 1.6fr 0.8fr 0.8fr;
          gap: 16px;
        }
      `}</style>

      <div className="dash-topbar" style={{ marginBottom: 28 }}>
        <div>
          <div style={{ width: 160, height: 34 }}>
            <SkeletonBlock height={34} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <SkeletonBlock height={36} radius={10} />
          <SkeletonBlock height={36} radius={10} />
          <SkeletonBlock height={36} radius={10} />
        </div>
      </div>

      <div className="dash-skeleton-grid-4">
        <SkeletonBlock height={116} />
        <SkeletonBlock height={116} />
        <SkeletonBlock height={116} />
        <SkeletonBlock height={116} />
      </div>

      <div className="dash-skeleton-grid-main">
        <SkeletonBlock height={260} />
        <SkeletonBlock height={260} />
        <SkeletonBlock height={260} />
      </div>

      <div className="dash-skeleton-grid-bottom">
        <SkeletonBlock height={240} />
        <SkeletonBlock height={240} />
        <SkeletonBlock height={240} />
      </div>
    </div>
  )
}

  return (
    <div className="hv-page">
      <style>{`
        .dash-topbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 20px;
          margin-bottom: 28px;
        }

        .dash-title-block {
          min-width: 0;
        }

        .dash-title-block .hv-page-title,
        .dash-title-block .hv-page-subtitle {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .dash-search {
          width: 360px;
          position: relative;
        }

        .dash-view-controls {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }

        .dash-segmented {
          height: 36px;
          display: inline-flex;
          align-items: center;
          padding: 3px;
          border: 1px solid var(--hv-border);
          border-radius: 10px;
          background: #fff;
        }

        .dash-segment {
          height: 28px;
          border: none;
          border-radius: 7px;
          background: transparent;
          color: var(--hv-text-secondary);
          font-size: 12px;
          font-weight: 800;
          padding: 0 11px;
          cursor: pointer;
        }

        .dash-segment--active {
          background: var(--hv-accent-light);
          color: var(--hv-accent);
        }

        .dash-portfolio-select {
          width: 220px;
          max-width: 220px;
          height: 36px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .dash-search-results {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid var(--hv-border);
  border-radius: 12px;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.16);
  padding: 8px;
  z-index: 50;
}

.dash-search-result {
  width: 100%;
  border: none;
  background: transparent;
  border-radius: 9px;
  padding: 10px 11px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  cursor: pointer;
  text-align: left;
}

.dash-search-result:hover {
  background: #F8FAFC;
}

.dash-search-result-title {
  font-size: 13px;
  font-weight: 800;
  color: var(--hv-text-primary);
}

.dash-search-result-subtitle {
  font-size: 11.5px;
  color: var(--hv-text-muted);
  margin-top: 2px;
}

.dash-search-result span {
  font-size: 10px;
  font-weight: 800;
  color: #4F46E5;
  background: #EEF2FF;
  border-radius: 999px;
  padding: 4px 7px;
}

.dash-search-empty {
  padding: 12px;
  font-size: 13px;
  color: var(--hv-text-muted);
}

        .dash-icon-btn {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          border: 1px solid var(--hv-border);
          background: #fff;
          color: var(--hv-text-secondary);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          position: relative;
        }

        .dash-badge {
          position: absolute;
          top: -5px;
          right: -5px;
          background: #4F46E5;
          color: #fff;
          font-size: 9px;
          font-weight: 800;
          border-radius: 999px;
          width: 16px;
          height: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .concept-card {
          background: #fff;
          border: 1px solid var(--hv-border);
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(15, 21, 35, 0.05);
        }

        .kpi-card {
          padding: 18px;
          min-height: 108px;
        }

        .dash-kpi-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 14px; margin-bottom: 16px; }
        .kpi-card-layout { display: flex; align-items: center; gap: 14px; height: 100%; }
        .kpi-card-icon { display: inline-flex; width: 44px; height: 44px; flex: 0 0 44px; align-items: center; justify-content: center; border-radius: 50%; }
        .kpi-card-icon.indigo { color: #4F46E5; background: #EEF2FF; }
        .kpi-card-icon.green { color: #059669; background: #E8F8F2; }
        .kpi-card-icon.blue { color: #2563EB; background: #EFF6FF; }
        .kpi-card-icon.red { color: #DC2626; background: #FEF2F2; }
        .kpi-card-copy { min-width: 0; flex: 1; }

        .kpi-label {
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 0;
          color: var(--hv-text-muted);
          margin-bottom: 10px;
        }

        .kpi-value {
          font-size: 28px;
          font-weight: 800;
          letter-spacing: -0.8px;
          color: var(--hv-text-primary);
          line-height: 1;
        }

        .kpi-sub {
          font-size: 11.5px;
          color: var(--hv-text-muted);
          margin-top: 8px;
        }

        .trend {
          color: #16A34A;
          font-size: 12px;
          font-weight: 800;
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }

        @media (max-width: 1200px) { .dash-kpi-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
        @media (max-width: 700px) { .dash-kpi-grid { grid-template-columns: 1fr; } }

        .panel-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 20px;
        }

        .panel-title {
          font-size: 14px;
          font-weight: 800;
          color: var(--hv-text-primary);
        }

        .mini-select {
          border: 1px solid var(--hv-border);
          background: #fff;
          border-radius: 8px;
          font-size: 12px;
          color: var(--hv-text-secondary);
          padding: 7px 10px;
        }

        .rev-row {
          display: grid;
          grid-template-columns: 150px 1fr 88px 44px;
          align-items: center;
          gap: 12px;
          margin-bottom: 18px;
        }

        .rev-name {
          font-size: 12.5px;
          font-weight: 700;
          color: var(--hv-text-primary);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .rev-track {
          height: 7px;
          background: #EEF2F7;
          border-radius: 999px;
          overflow: hidden;
        }

        .rev-fill {
          height: 100%;
          background: linear-gradient(90deg, #4F46E5, #7C3AED);
          border-radius: 999px;
        }

        .health-ring {
          width: 158px;
          height: 158px;
          border-radius: 50%;
          background: conic-gradient(#16A34A ${completionRate * 3.6}deg, #E5E7EB 0deg);
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto;
        }

        .health-ring-inner {
          width: 118px;
          height: 118px;
          background: #fff;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
        }

        .alert-row {
          display: flex;
          gap: 10px;
          padding: 9px 0;
          border-bottom: 1px solid var(--hv-border-light);
        }

        .alert-row:last-child {
          border-bottom: none;
        }

        .alert-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: #EF4444;
          margin-top: 7px;
          flex-shrink: 0;
        }

        .activity-row {
          display: grid;
          grid-template-columns: 28px 1fr auto;
          gap: 10px;
          align-items: center;
          padding: 9px 0;
        }

        .avatar-dot {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: #EEF2FF;
          color: #4F46E5;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: 800;
        }

        .deadline-row {
          display: grid;
          grid-template-columns: 42px 1fr auto;
          gap: 12px;
          padding: 11px 0;
          align-items: center;
        }

        .dash-alert-menu {
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  width: 340px;
  background: #fff;
  border: 1px solid var(--hv-border);
  border-radius: 14px;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.16);
  padding: 12px;
  z-index: 80;
}

.dash-alert-menu-title {
  font-size: 13px;
  font-weight: 800;
  color: var(--hv-text-primary);
  margin-bottom: 10px;
}

.dash-alert-menu-item {
  display: flex;
  gap: 10px;
  padding: 11px 8px;
  border-radius: 10px;
  cursor: pointer;
}

.dash-alert-menu-item:hover {
  background: #F8FAFC;
}

.dash-alert-menu-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-top: 6px;
  flex-shrink: 0;
}

.dash-alert-menu-empty {
  font-size: 13px;
  color: var(--hv-text-muted);
  padding: 10px 4px;
}


      `}</style>

      <div className="dash-topbar">
        <div className="dash-title-block">
          <h1 className="hv-page-title">{dashboardTitle}</h1>
          <p className="hv-page-subtitle">{dashboardSubtitle}</p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
<div className="dash-view-controls">
  <div className="dash-segmented" aria-label="Dashboard view">
    <button
      className={`dash-segment${dashboardView === 'overall' ? ' dash-segment--active' : ''}`}
      onClick={() => setDashboardView('overall')}
    >
      Overall
    </button>
    <button
      className={`dash-segment${dashboardView === 'portfolio' ? ' dash-segment--active' : ''}`}
      onClick={() => setDashboardView('portfolio')}
      disabled={portfolios.length === 0}
      title={portfolios.length === 0 ? 'Create a portfolio to use portfolio view' : 'View a portfolio dashboard'}
    >
      Portfolio
    </button>
  </div>

  {dashboardView === 'portfolio' && (
    <>
      <select
        className="hv-select dash-portfolio-select"
        value={selectedPortfolio?.id || ''}
        onChange={e => setSelectedPortfolioId(e.target.value)}
        title={selectedPortfolio?.name || 'Select portfolio'}
      >
        {activePortfolios.length === 0 && portfolios.length === 0 ? (
          <option value="">No portfolios</option>
        ) : (
          [...activePortfolios, ...portfolios.filter(portfolio => portfolio.status === 'archived')].map(portfolio => (
            <option key={portfolio.id} value={portfolio.id}>
              {portfolio.name}{portfolio.status === 'archived' ? ' (Archived)' : ''}
            </option>
          ))
        )}
      </select>

    </>
  )}

  {dashboardCurrencies.length > 0 && (
    <select
      className="hv-select dash-portfolio-select"
      value={selectedCurrency}
      onChange={e => setSelectedCurrency(e.target.value)}
      title="Revenue currency"
    >
      {dashboardCurrencies.map(currency => (
        <option key={currency} value={currency}>
          {currency}
        </option>
      ))}
    </select>
  )}
</div>

<div className="dash-search">
  <span className="hv-search-icon">
    <Search size={14} />
  </span>

  <input
    className="hv-search"
    placeholder="Search projects, deals, sponsors..."
    value={globalSearch}
    onFocus={() => setSearchFocused(true)}
    onChange={e => setGlobalSearch(e.target.value)}
    onKeyDown={e => {
      if (e.key === 'Enter' && globalSearch.trim()) {
        navigate(`/deals?search=${encodeURIComponent(globalSearch.trim())}`)
      }

      if (e.key === 'Escape') {
        setSearchFocused(false)
      }
    }}
  />

  {searchFocused && globalSearch.trim() && (
    <div className="dash-search-results">
      {globalResults.length === 0 ? (
        <div className="dash-search-empty">No matching results</div>
      ) : (
        globalResults.map(result => (
          <button
            key={`${result.type}-${result.id}`}
            className="dash-search-result"
            onMouseDown={e => {
              e.preventDefault()
              navigate(result.path)
              setGlobalSearch('')
              setSearchFocused(false)
            }}
          >
            <div>
              <div className="dash-search-result-title">{result.title}</div>
              <div className="dash-search-result-subtitle">{result.subtitle}</div>
            </div>
            <span>{result.type}</span>
          </button>
        ))
      )}
    </div>
  )}
</div>

<div style={{ position: 'relative' }}>
  <button
    className="dash-icon-btn"
    onClick={() => setAlertsOpen(prev => !prev)}
  >
    <Bell size={16} />
    {alerts.length > 0 && <span className="dash-badge">{alerts.length}</span>}
  </button>

  {alertsOpen && (
    <div className="dash-alert-menu">
      <div className="dash-alert-menu-title">Alerts</div>

      {alerts.length === 0 ? (
        <div className="dash-alert-menu-empty">No alerts right now.</div>
      ) : (
        alertPreview.map(alert => (
          <div
            key={alert.id}
            className="dash-alert-menu-item"
            onClick={() => navigate(alert.path || '/alerts')}
          >
            <span
              className="dash-alert-menu-dot"
              style={{ background: alert.severity === 'critical' ? '#EF4444' : '#F59E0B' }}
            />

            <div>
              <div style={{ fontSize: 12.5, fontWeight: 800 }}>
                {alert.title}
              </div>

              <div style={{ fontSize: 11.5, color: 'var(--hv-text-muted)', marginTop: 2 }}>
                {alert.subtitle}
              </div>

              <div style={{ fontSize: 11.5, color: alert.severity === 'critical' ? '#EF4444' : '#F59E0B', marginTop: 2 }}>
                {alert.detail}
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  )}
</div>

        </div>
      </div>

      <div className="dash-kpi-grid">
        {canViewFinancials && <div className="concept-card kpi-card">
          <div className="kpi-card-layout"><span className="kpi-card-icon green"><CircleDollarSign size={21} /></span><div className="kpi-card-copy"><div className="kpi-label">Total Revenue</div><div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><div>
              <div className="kpi-value">{formatMoney(totalRevenue, selectedCurrency)}</div>
              <div className="kpi-sub">Across {currencyDeals.length} {selectedCurrency} deals</div>
            </div>
            <div className="trend">Live</div>
          </div></div></div>
        </div>}

        <div className="concept-card kpi-card">
          <div className="kpi-card-layout"><span className="kpi-card-icon indigo"><BriefcaseBusiness size={21} /></span><div className="kpi-card-copy"><div className="kpi-label">Active Deals</div><div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><div>
              <div className="kpi-value">{currencyActiveDeals.length}</div>
              <div className="kpi-sub">{canViewFinancials ? `${formatMoney(activeProjectRevenue, selectedCurrency)} allocated across ${activeProjects} projects` : `${activeProjects} active projects`}</div>
            </div>
            <div className="trend">{currencyDeals.length} total</div>
          </div></div></div>
        </div>

        <div className="concept-card kpi-card">
          <div className="kpi-card-layout"><span className="kpi-card-icon blue"><Gauge size={21} /></span><div className="kpi-card-copy"><div className="kpi-label">Delivery Completion</div><div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><div>
              <div className="kpi-value">{completionRate}%</div>
              <div className="kpi-sub">{completedDeliveries} of {dashboardDeliveries.length} deliveries complete</div>
            </div>
            <div style={{
              width: 52,
              height: 52,
              borderRadius: '50%',
              background: `conic-gradient(#4F46E5 ${completionRate * 3.6}deg, #EEF2F7 0deg)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <div style={{ width: 38, height: 38, borderRadius: '50%', background: '#fff' }} />
            </div>
          </div></div></div>
        </div>

        <div className="concept-card kpi-card">
          <div className="kpi-card-layout"><span className="kpi-card-icon red"><AlertCircle size={21} /></span><div className="kpi-card-copy"><div className="kpi-label">Overdue Deliveries</div><div>
            <div className="kpi-value" style={{ color: overdueDeliveries.length ? '#DC2626' : 'var(--hv-text-primary)' }}>{overdueDeliveries.length}</div>
            <div className="kpi-sub" style={{ color: overdueDeliveries.length ? '#DC2626' : 'var(--hv-text-muted)' }}>
              {dueSoonDeliveries.length} due in the next 30 days
            </div>
          </div></div></div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 0.9fr', gap: '16px', marginBottom: '16px' }}>
        {canViewFinancials && <div className="concept-card" style={{ padding: '22px' }}>
          <div className="panel-header">
            <div className="panel-title">Revenue by Project</div>
            <select className="mini-select">
              <option>This Year</option>
            </select>
          </div>

          {projectRevenue.length === 0 ? (
            <div style={{ color: 'var(--hv-text-muted)', fontSize: '13px' }}>No project revenue yet.</div>
          ) : (
            projectRevenue.map(project => {
              const pct = Math.round((project.value / maxProjectRevenue) * 100)
              return (
                <div key={project.name} className="rev-row">
                  <div className="rev-name">{project.name}</div>
                  <div className="rev-track">
                    <div className="rev-fill" style={{ width: `${pct}%` }} />
                  </div>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--hv-text-primary)', textAlign: 'right' }}>
                    {formatMoney(project.value, selectedCurrency)}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--hv-text-muted)', textAlign: 'right' }}>
                    {pct}%
                  </div>
                </div>
              )
            })
          )}
        </div>}

        <div className="concept-card" style={{ padding: '22px' }}>
          <div className="panel-title" style={{ marginBottom: '20px' }}>Delivery Health</div>

          <div style={{ display: 'grid', gridTemplateColumns: '170px 1fr', gap: '18px', alignItems: 'center' }}>
            <div className="health-ring">
              <div className="health-ring-inner">
                <div style={{ fontSize: '34px', fontWeight: 800, letterSpacing: '-1px' }}>{completionRate}%</div>
                <div style={{ fontSize: '12px', color: 'var(--hv-text-muted)' }}>{portfolioMode ? 'Portfolio' : 'Overall'}</div>
              </div>
            </div>

            <div>
              <HealthItem color="#16A34A" value={deliveryHealth.delivered} label="Delivered" />
              <HealthItem color="#64748B" value={deliveryHealth.pending} label="Pending" />
              <HealthItem color="#EF4444" value={deliveryHealth.overdue} label="Overdue" />
            </div>
          </div>
        </div>

        <div className="concept-card" style={{ padding: '22px' }}>
          <div className="panel-header">
            <div className="panel-title">
              Alerts <span style={{ background: '#EF4444', color: '#fff', borderRadius: 999, padding: '2px 7px', fontSize: 10, marginLeft: 6 }}>{alerts.length}</span>
            </div>
          </div>

          {alerts.length === 0 ? (
            <div style={{ fontSize: '13px', color: 'var(--hv-text-muted)' }}>No alerts right now.</div>
          ) : (
            alertPreview.map(alert => (
              <div key={alert.id} className="alert-row" onClick={() => navigate(alert.path || '/alerts')} style={{ cursor: 'pointer' }}>
                <div className="alert-dot" style={{ background: alert.severity === 'critical' ? '#EF4444' : '#F59E0B' }} />
                <div>
                  <div style={{ fontSize: '12.5px', fontWeight: 800 }}>{alert.title}</div>
                  <div style={{ fontSize: '11.5px', color: 'var(--hv-text-muted)', marginTop: 2 }}>{alert.subtitle}</div>
                  <div style={{ fontSize: '11.5px', color: alert.severity === 'critical' ? '#EF4444' : '#F59E0B', marginTop: 2 }}>{alert.detail}</div>
                </div>
              </div>
            ))
          )}

          <button className="hv-btn hv-btn-ghost" style={{ marginTop: 14, color: '#4F46E5' }} onClick={() => navigate('/alerts')}>
            View all alerts →
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 0.8fr 0.8fr', gap: '16px' }}>
        <div className="concept-card" style={{ padding: '22px' }}>
          <div className="panel-title" style={{ marginBottom: 16 }}>Recent Activity</div>

          {recentActivity.length === 0 ? (
            <div style={{ fontSize: '13px', color: 'var(--hv-text-muted)' }}>No recent activity yet.</div>
          ) : (
            recentActivity.map((item, index) => (
              <div key={`${item.title}-${index}`} className="activity-row">
                <div className="avatar-dot">{index === 0 ? 'T' : index === 1 ? 'S' : index === 2 ? 'J' : 'H'}</div>
                <div>
                  <div style={{ fontSize: '12.5px', fontWeight: 700 }}>{item.title}</div>
                  <div style={{ fontSize: '11.5px', color: 'var(--hv-text-muted)' }}>{item.subtitle}</div>
                </div>
                <div style={{ fontSize: '11px', color: 'var(--hv-text-muted)' }}>{item.time}</div>
              </div>
            ))
          )}
        </div>

<div className="concept-card" style={{ padding: '22px' }}>
  <div className="panel-title" style={{ marginBottom: 16 }}>Upcoming Deadlines</div>

  {upcomingDeadlines.length === 0 ? (
    <div style={{ fontSize: '13px', color: 'var(--hv-text-muted)' }}>
      No upcoming deadlines.
    </div>
  ) : (
    upcomingDeadlines.map((item, index) => (
      <div key={`${item.title}-${index}`} className="deadline-row">
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '18px', fontWeight: 800 }}>
            {new Date(item.date).getDate()}
          </div>
          <div style={{ fontSize: '10px', color: 'var(--hv-text-muted)', textTransform: 'uppercase' }}>
            {new Date(item.date).toLocaleDateString('en-GB', { month: 'short' })}
          </div>
        </div>

        <div>
          <div style={{ fontSize: '12.5px', fontWeight: 800 }}>
            {item.title}
          </div>
          <div style={{ fontSize: '11.5px', color: 'var(--hv-text-muted)' }}>
            {item.subtitle}
          </div>
        </div>

        <div style={{ fontSize: '11.5px', color: 'var(--hv-text-muted)' }}>
          {item.type === 'deal' ? 'Deal' : 'Delivery'}
        </div>
      </div>
    ))
  )}

  <button
    className="hv-btn hv-btn-ghost"
    style={{ marginTop: 12, color: '#4F46E5' }}
    onClick={() => navigate('/calendar')}
  >
    View calendar →
  </button>
</div>

        <div className="concept-card" style={{ padding: '22px' }}>
          <div className="panel-title" style={{ marginBottom: 16 }}>Quick Status</div>

          <StatusLine icon={<CheckCircle2 size={15} />} label="Clients" value={clients.length} />
          <StatusLine icon={<CalendarDays size={15} />} label="Projects" value={dashboardProjects.length} />
          <StatusLine icon={<AlertCircle size={15} />} label="Alerts" value={alerts.length} />

          {!portfolioMode && (
            <button
              className="hv-btn hv-btn-primary"
              style={{ width: '100%', marginTop: 20 }}
              onClick={() => navigate('/deals')}
            >
              View all deals →
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function HealthItem({ color, value, label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
      <div style={{ width: 9, height: 9, borderRadius: '50%', background: color }} />
      <div>
        <div style={{ fontSize: 20, fontWeight: 800, color }}>{value}</div>
        <div style={{ fontSize: 12, color: 'var(--hv-text-muted)' }}>{label}</div>
      </div>
    </div>
  )
}

function StatusLine({ icon, label, value }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '12px 0',
      borderBottom: '1px solid var(--hv-border-light)'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--hv-text-secondary)' }}>
        {icon}
        <span style={{ fontSize: 13, fontWeight: 700 }}>{label}</span>
      </div>
      <span style={{ fontSize: 13, fontWeight: 800 }}>{value}</span>
    </div>
  )
}
